import React, { useRef } from 'react';
import { CirclePlus, MoreVertical, ChevronUp, ChevronDown, Trash2 } from 'lucide-react';
import { FolderData } from "../../types/PanoptoData";
import { triggerPiepeline } from '../../services/api/virtualAssistant';

interface ContextMenu {
  visible: boolean;
  x: number;
  y: number;
  panoptoId: number;
}

interface PanoptoSidebarProps {
  sortedFolders: FolderData[];
  selectedFolder: number;
  setselectedFolder: (id: number) => void;
  contextMenu: ContextMenu;
  setContextMenu: React.Dispatch<React.SetStateAction<ContextMenu>>;
  contextMenuRef: React.RefObject<HTMLDivElement>;
  addNewPanoptoFolder: () => void;
  setPanoptos: React.Dispatch<React.SetStateAction<FolderData[]>>;
  triggerAutoSave: () => void;
  maxFolders: number;
  currentStatus: null | {
    progress: number;
    status: string;
    error?: string;
  };
  systemConfig: { Id: string; ConfigValue: string; }[];
  assistant: any;
  isPanoptoFullLoad: boolean;
  setPendingSave: React.Dispatch<React.SetStateAction<boolean>>;
  setCustomModal: React.Dispatch<React.SetStateAction<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "loading" | "confirmation" | "warning";
    onConfirm?: () => void;
  }>>;
  fetchPipelineData: () => void;
  panoptos: FolderData[];
}

const PanoptoSidebar: React.FC<PanoptoSidebarProps> = ({
  sortedFolders,
  selectedFolder,
  setselectedFolder,
  contextMenu,
  setContextMenu,
  contextMenuRef,
  addNewPanoptoFolder,
  setPanoptos,
  triggerAutoSave,
  maxFolders,
  currentStatus,
  systemConfig,
  assistant,
  isPanoptoFullLoad,
  setPendingSave,
  setCustomModal,
  fetchPipelineData,
  panoptos
}) => {
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  const handleContextMenu = (e: React.MouseEvent, panoptoId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      panoptoId
    });
  };

  const closeContextMenu = () => {
    setContextMenu(prev => ({ ...prev, visible: false }));
  };

  const movePanoptoFolder = (folderId: number, direction: 'up' | 'down') => {
    setPanoptos((prevFolders) => {
      const sortedFolders = [...prevFolders].sort((a, b) => a.order - b.order);
      const currentIndex = sortedFolders.findIndex((folder) => folder.id === folderId);
      
      if (
        (direction === 'up' && currentIndex === 0) ||
        (direction === 'down' && currentIndex === sortedFolders.length - 1)
      ) {
        return prevFolders;
      }
      
      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

      const updatedFolders = sortedFolders.map((folder, index) => {
        if (index === currentIndex) {
          return { ...folder, order: sortedFolders[newIndex].order };
        }
        if (index === newIndex) {
          return { ...folder, order: sortedFolders[currentIndex].order };
        }
        return folder;
      });

      return updatedFolders;
    });
    triggerAutoSave();
    closeContextMenu();
  };

  const showDeleteConfirmationModal = (message: string, onConfirm: () => void) => {
    setCustomModal({
      isOpen: true,
      title: "Delete Confirmation",
      message,
      type: "confirmation",
      onConfirm,
    });
  };

  const deletePanoptoFolder = (panoptoId: number) => {
    let warningMessage = 'Are you sure you want to delete panopto? This action cannot be undone.';
    showDeleteConfirmationModal(warningMessage, async () => {

      setCustomModal((prev) => ({
        ...prev,
        isOpen: false,
      }));
          
      setCustomModal({
        isOpen: true,
        title: "Deleting...",
        message: warningMessage,
        type: "loading",
      });
    
      try {
        const folderToDelete = panoptos.find(p => p.id === panoptoId);
        const isConnected = folderToDelete?.connectionState === 'connected';
        const pipelineStatus = currentStatus?.status;
        
        setPanoptos(prev => {
          const filtered = prev.filter(c => c.id !== panoptoId);
          return filtered.map((panopto, index) => ({
            ...panopto,
            order: index + 1
          }));
        });
        
        if (selectedFolder === panoptoId) {
          const remainingFolders = panoptos.filter(c => c.id !== panoptoId);
          setselectedFolder(remainingFolders[0]?.id || 0);
        }
        
        if (isConnected) {
          triggerAutoSave();
          setTimeout(() => {
            let queue_name = systemConfig.find(item => item.Id === 'panopto_queue_name')?.ConfigValue ?? '';
            const run_type = 'Adhoc';
            const full_load_value = String(isPanoptoFullLoad) === 'true' ? 'false' : 'true';
            const res = triggerPiepeline(assistant.app_id, run_type, queue_name, full_load_value);
            setPendingSave(false);
            setTimeout(() => {
              fetchPipelineData();
              setTimeout(() => {
                fetchPipelineData();
              }, 500);
            }, 500); 
            setCustomModal({
              isOpen: true,
              title: "Success!",
              message: 'Panopto deleted successfully',
              type: "success",
            });
          }, 20 * 1000);
        } else {
          setCustomModal({
            isOpen: true,
            title: "Success!",
            message: 'Panopto deleted successfully',
            type: "success",
          });
        }
        
      } catch (error) {
        console.log("Delete failed", error);
      }
    });
                
    closeContextMenu();
  };

  const handleDragStart = (e: React.DragEvent, position: number) => {
    e.stopPropagation();
    dragItem.current = position;
    e.currentTarget.classList.add('opacity-50');
  };

  const handleDragEnter = (e: React.DragEvent, position: number) => {
    e.preventDefault();
    e.stopPropagation();
    dragOverItem.current = position;
    e.currentTarget.classList.add('bg-gray-50');
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.currentTarget.classList.remove('bg-gray-50');
  };

  const handleDragEnd = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    e.currentTarget.classList.remove('opacity-50');

    if (dragItem.current !== null && dragOverItem.current !== null) {
      const copyListItems = [...sortedFolders];
      const dragItemContent = copyListItems[dragItem.current];
      
      copyListItems.splice(dragItem.current, 1);
      copyListItems.splice(dragOverItem.current, 0, dragItemContent);
      
      const reorderedItems = copyListItems.map((item, index) => ({
        ...item,
        order: index + 1,
      }));

      setPanoptos(reorderedItems);
    }

    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  return (
    <>
      <div className="w-64 border-r border-gray-200 overflow-y-auto">
        {sortedFolders.map((folder, index) => (
          <div
            key={folder.id}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragEnter={(e) => handleDragEnter(e, index)}
            onDragLeave={handleDragLeave}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            className={`flex items-center justify-between p-4 cursor-move hover:bg-gray-50 
              ${selectedFolder === folder.id ? 'bg-gray-100' : ''}
              transition-opacity duration-200`}
          >
            <div 
              className="flex items-center space-x-3"
              onClick={() => setselectedFolder(folder.id)}
            >
              <img src="/panopto-logo.svg" alt="Panopto Logo" className="h-5 w-5" />
              <span className="text-sm" title={folder?.panopto_name}>
                <div
                  className="cursor-pointer"
                  dangerouslySetInnerHTML={{ __html: folder.panopto_name || `New Panopto Folder ${folder.order}` }}
                />
              </span>
            </div>
            <button
              className="hover:bg-gray-100 p-1 rounded"
              onClick={(e) => {
                e.stopPropagation();
                handleContextMenu(e, folder.id);
              }}
            >
              <MoreVertical className="h-4 w-4 text-gray-400" />
            </button>
          </div>
        ))}
        
        {sortedFolders.length < maxFolders && (
          <div className="p-4">
            <button
              onClick={addNewPanoptoFolder}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
            >
              <CirclePlus className="h-6 w-6 text-gray-500" />
            </button>
          </div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          ref={contextMenuRef}
          className="fixed bg-white shadow-lg rounded-lg py-1 z-50"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button
            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center space-x-2"
            onClick={() => movePanoptoFolder(contextMenu.panoptoId, 'up')}
          >
            <ChevronUp className="h-4 w-4" />
            <span>Move Up</span>
          </button>
          <button
            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center space-x-2"
            onClick={() => movePanoptoFolder(contextMenu.panoptoId, 'down')}
          >
            <ChevronDown className="h-4 w-4" />
            <span>Move Down</span>
          </button>
          <button
            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center space-x-2 text-red-600"
            onClick={() => deletePanoptoFolder(contextMenu.panoptoId)}
          >
            <Trash2 className="h-4 w-4" />
            <span>Delete</span>
          </button>
        </div>
      )}
    </>
  );
};

export default PanoptoSidebar;